/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, ReactNode } from "react";
import { ErrorFactory } from "@core/lib/error-handling";

interface FeatureFlags {
  // Product features
  enableDatePolls: boolean;
  enableFormPolls: boolean;
  enableQuizz: boolean;

  // UI features
  enableDarkMode: boolean;
  enableAdvancedFilters: boolean;
  enableExportFeatures: boolean;

  // Beta features
  enableBetaFeatures: boolean;
  enableNewDashboard: boolean;

  // Analytics
  enableAnalytics: boolean;
  enableErrorTracking: boolean;

  // Feature Flags (Isolation of incomplete features)
  enableOneResponsePerPerson: boolean;
  enableVisibilityVotersOnly: boolean;
  enableVisibilityCreatorOnly: boolean;
  enableEnterprisePlan: boolean;
  enableUserDataEmailStatus: boolean;
  enableUserDataImproveToggle: boolean;
  enableMaybeVotes: boolean;
  enableEditAfterSubmit: boolean;
  /** Redesign polls V2: new dashboard card + vote + results modes (2026-04-22). */
  enableRedesignPollsV2: boolean;
  /** Pattern B "Settings Surfacing" : récap pré-publication avant `createPoll`. */
  enablePrePublishModal: boolean;
  /**
   * Éditeur d'horaires V2 (chantier 2026-09-12, cf. Docs/SPECS/2026-09-12-
   * editeur-horaires-v2-decisions.md, D34e). V1 (`TimeSlotsTable`) reste le
   * défaut partout — ce flag n'est PAS destiné à être vrai par défaut en
   * production tant que le chantier n'est pas validé (D23) :
   *   - `VITE_ENABLE_SCHEDULE_EDITOR_V2=true` l'active, PROD comprise
   *     (c'est le mécanisme de bascule finale par environnement, D23) ;
   *   - hors production seulement : `?scheduleEditor=v2`/`v1` (URL) et
   *     `localStorage.doodates.enableScheduleEditorV2` permettent de tester
   *     sans redéployer. Jamais honorés en production (on ne veut pas qu'un
   *     paramètre d'URL bascule un utilisateur de prod sur un éditeur non
   *     encore validé).
   */
  enableScheduleEditorV2: boolean;
}

const defaultFlags: FeatureFlags = {
  enableDatePolls: true,
  enableFormPolls: true,
  enableQuizz: false, // Coming soon
  enableDarkMode: true,
  enableAdvancedFilters: true,
  enableExportFeatures: true,
  enableBetaFeatures: false,
  enableNewDashboard: false,
  enableAnalytics: true,
  enableErrorTracking: true,

  // Feature Flags (Isolation)
  enableOneResponsePerPerson: false,
  enableVisibilityVotersOnly: false,
  enableVisibilityCreatorOnly: false,
  enableEnterprisePlan: false,
  enableUserDataEmailStatus: false,
  enableUserDataImproveToggle: false,
  enableMaybeVotes: false,
  enableEditAfterSubmit: false,
  enableRedesignPollsV2: true,
  // Pre-publish modal (Pattern B). ON par défaut (activé 2026-05-22).
  //  - opt-out : env `VITE_ENABLE_PRE_PUBLISH_MODAL=false`
  //  - localStorage `doodates.enablePrePublishModal=false` (dev/E2E)
  enablePrePublishModal: (() => {
    if (typeof window === "undefined") return true;
    try {
      const lsVal = window.localStorage.getItem(
        "doodates.enablePrePublishModal",
      );
      if (lsVal === "false") return false;
    } catch {
      /* private mode */
    }
    const envFlag =
      typeof import.meta !== "undefined" &&
      import.meta.env?.VITE_ENABLE_PRE_PUBLISH_MODAL === "false";
    if (envFlag) return false;
    return true;
  })(),
  enableScheduleEditorV2: (() => {
    // "Production" ici veut dire le DOMAINE de prod (doodates.com), pas
    // `import.meta.env.PROD` : un déploiement preview Vercel tourne aussi
    // un build de production (`vite build`), donc PROD vaudrait déjà true
    // sur les previews — cette confusion aurait rendu ?scheduleEditor=v2
    // inopérant précisément là où on veut tester (D34e, D23). Même logique
    // de distinction domaine que RedirectToMainApp.tsx.
    const isRealProductionDomain =
      typeof window !== "undefined" &&
      /(^|\.)doodates\.com$/.test(window.location.hostname);

    // Hors production seulement : URL puis localStorage (D34e). Jamais lus
    // en production — un lien ou un localStorage résiduel ne doit jamais
    // faire basculer un utilisateur de prod sur un éditeur non validé.
    if (!isRealProductionDomain && typeof window !== "undefined") {
      try {
        const fromUrl = new URLSearchParams(window.location.search).get(
          "scheduleEditor",
        );
        if (fromUrl === "v2") return true;
        if (fromUrl === "v1") return false;
      } catch {
        /* URLSearchParams absent (SSR très tôt) */
      }
      try {
        const fromStorage = window.localStorage.getItem(
          "doodates.enableScheduleEditorV2",
        );
        if (fromStorage === "true") return true;
        if (fromStorage === "false") return false;
      } catch {
        /* private mode */
      }
    }

    // Mécanisme de bascule finale par environnement (D23) : peut valoir
    // true en production une fois le chantier validé. Faux par défaut.
    return (
      typeof import.meta !== "undefined" &&
      import.meta.env?.VITE_ENABLE_SCHEDULE_EDITOR_V2 === "true"
    );
  })(),
};

interface FeatureFlagsContextType {
  flags: FeatureFlags;
  isEnabled: (feature: keyof FeatureFlags) => boolean;
  updateFlag: (feature: keyof FeatureFlags, value: boolean) => void;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(
  undefined,
);

interface FeatureFlagsProviderProps {
  children: ReactNode;
  initialFlags?: Partial<FeatureFlags>;
}

export const FeatureFlagsProvider: React.FC<FeatureFlagsProviderProps> = ({
  children,
  initialFlags = {},
}) => {
  const [flags, setFlags] = React.useState<FeatureFlags>({
    ...defaultFlags,
    ...initialFlags,
  });

  const isEnabled = (feature: keyof FeatureFlags): boolean => {
    return flags[feature] || false;
  };

  const updateFlag = (feature: keyof FeatureFlags, value: boolean) => {
    setFlags((prev) => ({ ...prev, [feature]: value }));
  };

  return (
    <FeatureFlagsContext.Provider value={{ flags, isEnabled, updateFlag }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlags = (): FeatureFlagsContextType => {
  const context = useContext(FeatureFlagsContext);
  if (context === undefined) {
    throw ErrorFactory.validation(
      "useFeatureFlags must be used within a FeatureFlagsProvider",
      "Erreur de configuration du contexte",
    );
  }
  return context;
};
